import {
  CaretDown,
  ChartLine,
  ChartLineUp,
  Info,
  Lightning,
  MinusCircle,
  Package,
  PencilSimple,
  Question,
  ShoppingCart,
  TrendDown,
  TrendUp,
  TShirt,
  Warning,
} from "@phosphor-icons/react"

import { useForecastBreakdownDetail, usePhaseOutSellDownDetail } from "~/hooks/useVariantTableHooks"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import {
  getAlerts,
  getForecastBreakdown,
  getNpiForecastBreakdown,
  isNoDemandPhaseOut,
} from "~/utils/forecast-alert-utils"
import {
  salesVsLyByWindow,
  salesVsLyCoverageNote,
  type SalesVsLyWindow,
} from "~/utils/forecast-decomposition"
import { formatNumber, formatProductName } from "~/utils/formatting"
import { cn, formatVariantId, roundNumber, toPercent } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { Badge } from "~/components/reusable/Badge"
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "~/components/reusable/hovercard/HoverCard"
import { BreakdownHoverCard } from "~/components/reusable/hovercard/BreakdownHoverCard"
import HoverCardExpandableRow from "~/components/reusable/hovercard/HoverCardExpandableRow"
import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import { Accordion } from "~/components/reusable/Accordion"
import ImputationChart from "~/components/reusable/ImputationChart"
import MiniTable from "~/components/reusable/miniTable/MiniTable"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import PreorderFactorsChart from "~/components/reusable/PreorderFactorsChart"
import ProductImage from "~/components/reusable/ProductImage"
import { Skeleton } from "~/components/reusable/Skeleton"
import { Tooltip } from "~/components/reusable/Tooltip"

type Props = {
  variants: IVariantSummary[]
  forecastAlertThreshold: number
  /** Replaces the default alert-badge trigger, for callers that need a narrow cell. */
  trigger?: React.ReactNode
}

/** Places the breakdown's header and body; the hover card stacks them, the popover pins the header. */
export type BreakdownLayout = (parts: {
  title: string
  identity: React.ReactNode
  body: React.ReactNode
}) => React.ReactNode

type SectionsProps = {
  variants: IVariantSummary[]
  forecastAlertThreshold: number
  layout: BreakdownLayout
  /**
   * Marks the drilldown behind each alert the variant raises (Pre-orders growth, Model trend) with
   * a warning icon and `fg-warning-text` on its label and value, left collapsed.
   */
  highlightAlerts?: boolean
}

export function BreakdownHeader({
  title,
  identity,
  action,
}: {
  title: string
  identity: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="shrink-0 px-4 py-3 border-b border-base">
      <div className="flex items-center justify-between gap-2">
        <span className="label-base text-fg-base">{title}</span>
        {action}
      </div>
      {identity}
    </div>
  )
}

const stackedLayout: BreakdownLayout = ({ title, identity, body }) => (
  <>
    <BreakdownHeader title={title} identity={identity} />
    {body}
  </>
)

export function ForecastBreakdownHoverCard({
  variants,
  forecastAlertThreshold,
  trigger: triggerOverride,
}: Props) {
  const isMultiVariant = variants.length > 1
  const singleVariant = !isMultiVariant ? variants[0] : null
  const isNpi = !!singleVariant?.isNpi

  if (isNpi && singleVariant) {
    return <NpiForecastBreakdownHoverCard variant={singleVariant} trigger={triggerOverride} />
  }

  const alertCount = isMultiVariant
    ? variants.filter((v) => {
        const a = getAlerts(v, forecastAlertThreshold)
        return a && a.length > 0
      }).length
    : 0
  const singleAlerts = isMultiVariant ? [] : (getAlerts(variants[0], forecastAlertThreshold) ?? [])

  const trigger =
    triggerOverride ??
    (isMultiVariant ? (
      alertCount > 0 ? (
        <div className="text-red-500">{alertCount} Alerts</div>
      ) : (
        <div className="text-fg-muted paragraph-base">-</div>
      )
    ) : singleAlerts.length === 0 ? (
      <div className="items-center flex">
        <Info size={14} className="text-fg-muted" />
      </div>
    ) : (
      <div className="cursor-pointer flex items-center gap-1 flex-wrap">
        {singleAlerts.map((alert) => (
          <Badge key={alert} size="xs" variant="yellow">
            <Warning format="outline" weight="bold" className="mr-1 inline" size={12} />
            {alert}
          </Badge>
        ))}
      </div>
    ))

  return (
    <BreakdownHoverCard
      trigger={trigger}
      className="w-96 [&_[data-radix-accordion-content]]:!transition-none"
      scrollable
    >
      <ForecastBreakdownSections
        variants={variants}
        forecastAlertThreshold={forecastAlertThreshold}
        layout={stackedLayout}
      />
    </BreakdownHoverCard>
  )
}

/**
 * The breakdown's content, mounted only while a surface is open. NPI variants get their own
 * breakdown.
 */
export function ForecastBreakdownSections(props: SectionsProps) {
  const singleVariant = props.variants.length > 1 ? null : props.variants[0]
  return singleVariant?.isNpi ? (
    <NpiBreakdownSections variant={singleVariant} layout={props.layout} />
  ) : (
    <RegularBreakdownSections {...props} />
  )
}

function RegularBreakdownSections({
  variants,
  forecastAlertThreshold,
  layout,
  highlightAlerts = false,
}: SectionsProps) {
  const isMultiVariant = variants.length > 1
  const singleVariant = !isMultiVariant ? variants[0] : null

  const variantIds = variants.map((v) => v.variantId)
  const { data: detailMap } = useForecastBreakdownDetail(variantIds, true)
  const breakdownRows = detailMap ? Array.from(detailMap.values()).flat() : []

  const {
    sales,
    forecast,
    preOrderChange,
    totalUnknownPreOrders,
    stockoutTotal,
    stockoutWeeks,
    missingSalesTotal,
    missingSalesWeeks,
    observationWeeks,
    manualAdjustment,
    fashionForecast,
    excludedFromForecast,
    phasingOutEffect,
    phasingOutWeeks,
    unexplained,
    totalGrowth,
  } = getForecastBreakdown(variants)

  const pct = (v: number) => (sales > 0 ? toPercent(v / sales, 1) : "-")

  const phaseOutNoDemandOnly = variants.length > 0 && variants.every(isNoDemandPhaseOut)

  const decomposition = singleVariant?.forecastDecomposition ?? null
  const salesVsLyWindows = (
    [
      ["5W", 1],
      ["13W", 3],
      ["26W", 6],
      ["52W", 12],
    ] as [string, SalesVsLyWindow][]
  ).map(([weekLabel, window]) => {
    const aggregate = salesVsLyByWindow(variants, window)
    return {
      weekLabel,
      pct: aggregate.pct,
      coverage: salesVsLyCoverageNote(aggregate),
    }
  })

  const modelForecastPct = sales > 0 ? (unexplained / sales) * 100 : 0
  // The same tests getAlerts() runs for "Model trend" and the pre-order alert.
  const exceeds = (units: number) => sales > 0 && Math.abs(units / sales) >= forecastAlertThreshold
  const preorderAlert = highlightAlerts && exceeds(preOrderChange + totalUnknownPreOrders)
  const modelTrendAlert = highlightAlerts && exceeds(unexplained)
  const alertIcon = <Warning size={14} weight="fill" className="text-fg-warning-icon" />
  const demandTrendPct = decomposition?.demandTrendPct ?? null
  const hasDemandTrend = demandTrendPct != null
  const hasAggregatedSalesData = salesVsLyWindows.some((w) => w.pct != null)
  const hasDecomposition = hasDemandTrend || hasAggregatedSalesData
  // Model trend = portion of model forecast supported by demand trend (clamped to same sign)
  const modelTrendPct = hasDemandTrend
    ? demandTrendPct * modelForecastPct > 0
      ? Math.abs(demandTrendPct) < Math.abs(modelForecastPct)
        ? demandTrendPct
        : modelForecastPct
      : 0
    : modelForecastPct
  const modelTrendUnits = (sales * modelTrendPct) / 100
  const unexplainedUnits = unexplained - modelTrendUnits

  return layout({
    title: "Demand breakdown",
    identity: singleVariant && <VariantIdentityRow variant={singleVariant} />,
    body: (
      <>
        <HoverCardRowWrapper>
          <HoverCardRow
            icon={<TrendUp size={14} />}
            label="Demand (next 52w)"
            value={formatNumber(Math.round(forecast))}
          />
        </HoverCardRowWrapper>
        <div className="border-t border-base">
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<TrendDown size={14} />}
              label={`${formatName("sale", true, true)} (last 52w)`}
              value={formatNumber(Math.round(sales))}
            />
          </HoverCardRowWrapper>
        </div>
        <div className="border-t border-base">
          <Accordion
            type="multiple"
            defaultValue={["total-growth"]}
            className="[&>div]:border-none"
          >
            <HoverCardExpandableRow
              value="total-growth"
              hideCaret
              HoverCardRow={
                <HoverCardRow
                  icon={<ChartLineUp size={14} />}
                  label={
                    <>
                      Total growth{" "}
                      <CaretDown
                        size={12}
                        className="inline transition-transform duration-200 group-data-[state=open]/expandable:rotate-180"
                      />
                    </>
                  }
                  value={`${formatNumber(Math.round(totalGrowth))} (${pct(totalGrowth)})`}
                  className="text-fg-base"
                  classNameWrapper="font-medium"
                />
              }
            >
              <div className="bg-subtle">
                <HoverCardRowWrapper className="bg-transparent pl-4">
                  {!phaseOutNoDemandOnly && preOrderChange + totalUnknownPreOrders !== 0 && (
                    <Accordion type="multiple" className="[&>div]:border-none">
                      <HoverCardExpandableRow
                        hideCaret
                        HoverCardRow={
                          <HoverCardRow
                            icon={preorderAlert ? alertIcon : <ShoppingCart size={14} />}
                            label={
                              <span
                                className={cn(
                                  "font-normal",
                                  preorderAlert && "text-fg-warning-text",
                                )}
                              >
                                {formatName("preorder", true, true)} growth{" "}
                                <CaretDown
                                  size={12}
                                  className="inline transition-transform duration-200 group-data-[state=open]/expandable:rotate-180"
                                />
                              </span>
                            }
                            value={`${formatNumber(Math.round(preOrderChange + totalUnknownPreOrders))} (${pct(preOrderChange + totalUnknownPreOrders)})`}
                            className={cn(
                              "font-medium",
                              preorderAlert
                                ? "text-fg-warning-text"
                                : exceeds(preOrderChange + totalUnknownPreOrders) &&
                                    "text-badge-orange-fg-text",
                            )}
                            valueExtra={
                              <HoverCard>
                                <HoverCardTrigger asChild>
                                  <button className="text-fg-muted hover:text-fg-base">
                                    <Info size={12} />
                                  </button>
                                </HoverCardTrigger>
                                <HoverCardContent className="w-155 p-3" side="right">
                                  <PreorderFactorsChart
                                    data={breakdownRows}
                                    lastYearPreOrders={variants.reduce(
                                      (sum, v) => sum + (v.lastYearPreOrders ?? 0),
                                      0,
                                    )}
                                  />
                                </HoverCardContent>
                              </HoverCard>
                            }
                          />
                        }
                      >
                        <HoverCardRow
                          icon={<span className="w-3.5" />}
                          label={`  Known ${formatName("preorder", true)} growth`}
                          value={formatNumber(Math.round(preOrderChange))}
                        />
                        {totalUnknownPreOrders !== 0 && (
                          <HoverCardRow
                            icon={<span className="w-3.5" />}
                            label={`  Forecasted ${formatName("preorder", true)}`}
                            value={formatNumber(Math.round(totalUnknownPreOrders))}
                          />
                        )}
                      </HoverCardExpandableRow>
                    </Accordion>
                  )}
                  {stockoutTotal > 0 && (
                    <HoverCardRow
                      icon={<Package size={14} />}
                      label={`Estimated demand during stockout (${stockoutWeeks}w)`}
                      value={`${formatNumber(Math.round(stockoutTotal))} (${pct(stockoutTotal)})`}
                      valueExtra={
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <button className="text-fg-muted hover:text-fg-base">
                              <Info size={12} />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-155 p-3" side="right">
                            <ImputationChart
                              data={breakdownRows}
                              variantIds={variantIds}
                              valueKey="stockoutImputation"
                              title={`Estimated demand during stockout breakdown`}
                              seriesName={`Estimated demand during stockout`}
                              subtitleLabel="Stockout detected in"
                              weeks={stockoutWeeks}
                              observationWeeks={observationWeeks}
                              total={stockoutTotal}
                            />
                          </HoverCardContent>
                        </HoverCard>
                      }
                    />
                  )}
                  {missingSalesTotal > 0 && (
                    <HoverCardRow
                      icon={<Question size={14} />}
                      label={`Estimated un-observed demand (${missingSalesWeeks}w)`}
                      value={`${formatNumber(Math.round(missingSalesTotal))} (${pct(missingSalesTotal)})`}
                      valueExtra={
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <button className="text-fg-muted hover:text-fg-base">
                              <Info size={12} />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-155 p-3" side="right">
                            <ImputationChart
                              data={breakdownRows}
                              variantIds={variantIds}
                              valueKey="missingSalesImputation"
                              title={`Estimated un-observed demand breakdown`}
                              seriesName={`Estimated un-observed demand`}
                              subtitleLabel="Missing data in"
                              weeks={missingSalesWeeks}
                              observationWeeks={observationWeeks}
                              total={missingSalesTotal}
                            />
                          </HoverCardContent>
                        </HoverCard>
                      }
                    />
                  )}
                  {Math.round(manualAdjustment) !== 0 && (
                    <HoverCardRow
                      icon={<PencilSimple size={14} />}
                      label="Manual adjustment"
                      value={`${formatNumber(Math.round(manualAdjustment))} (${pct(manualAdjustment)})`}
                    />
                  )}
                  {Math.round(excludedFromForecast) !== 0 && (
                    <HoverCardRow
                      icon={<MinusCircle size={14} />}
                      label={`${formatName("sale", true, true)} not used for demand`}
                      value={`${formatNumber(Math.round(excludedFromForecast))} (${pct(excludedFromForecast)})`}
                      valueExtra={
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <button className="text-fg-muted hover:text-fg-base">
                              <Info size={12} />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-72 p-3" side="right">
                            <div className="label-sm text-fg-base mb-1">
                              {formatName("sale", true, true)} not used for demand
                            </div>
                            <div className="paragraph-sm text-fg-muted">
                              Some past {formatName("sale", true)} are excluded from the base used
                              to estimate the demand. This causes a difference between the expected
                              demand and last year&apos;s {formatName("sale", true)}.
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      }
                    />
                  )}
                  {Math.round(fashionForecast) !== 0 && (
                    <HoverCardRow
                      icon={<TShirt size={14} />}
                      label="Fashion Demand"
                      value={`${formatNumber(Math.round(fashionForecast))} (${pct(fashionForecast)})`}
                    />
                  )}
                  {!phaseOutNoDemandOnly && (
                    <Accordion type="multiple" className="[&>div]:border-none">
                      <HoverCardExpandableRow
                        hideCaret
                        HoverCardRow={
                          <HoverCardRow
                            icon={modelTrendAlert ? alertIcon : <ChartLine size={14} />}
                            label={
                              <span
                                className={cn(
                                  "font-normal",
                                  modelTrendAlert && "text-fg-warning-text",
                                )}
                              >
                                Model trend{" "}
                                {hasDecomposition && (
                                  <CaretDown
                                    size={12}
                                    className="inline transition-transform duration-200 group-data-[state=open]/expandable:rotate-180"
                                  />
                                )}
                              </span>
                            }
                            value={`${formatNumber(Math.round(modelTrendUnits))} (${roundNumber(modelTrendPct, 1)}%)`}
                            className={modelTrendAlert ? "text-fg-warning-text" : undefined}
                            valueExtra={
                              <InfoTooltip
                                body={`Percentage change of the last weeks of ${formatName("sale", true)} vs the same period last year, excluding ${formatName("preorder", true)}.`}
                                className="h-3.5 w-3.5 text-fg-muted"
                              />
                            }
                          />
                        }
                      >
                        {hasDecomposition && (
                          <>
                            {salesVsLyWindows.map(({ weekLabel, pct, coverage }) => (
                              <HoverCardRow
                                key={weekLabel}
                                icon={<span className="w-3.5" />}
                                label={`  ${formatName("sale", true, true)} vs LY (last ${weekLabel})`}
                                description={coverage}
                                value={pct != null ? `${roundNumber(pct, 1)}%` : "-"}
                              />
                            ))}
                            {decomposition?.trendCagr2yrPct != null && (
                              <HoverCardRow
                                icon={<span className="w-3.5" />}
                                label="  2 year trend"
                                value={`${roundNumber(decomposition.trendCagr2yrPct, 1)}%`}
                              />
                            )}
                            {decomposition?.trendCagr3yrPct != null && (
                              <HoverCardRow
                                icon={<span className="w-3.5" />}
                                label="  3 year trend"
                                value={`${roundNumber(decomposition.trendCagr3yrPct, 1)}%`}
                              />
                            )}
                          </>
                        )}
                      </HoverCardExpandableRow>
                    </Accordion>
                  )}
                  {!phaseOutNoDemandOnly &&
                    hasDecomposition &&
                    Math.round(unexplainedUnits) !== 0 && (
                      <HoverCardRow
                        icon={<Lightning size={14} />}
                        label="Other model signals"
                        value={`${formatNumber(Math.round(unexplainedUnits))} (${roundNumber(modelForecastPct - modelTrendPct, 1)}%)`}
                        className={
                          sales > 0 && Math.abs(unexplainedUnits / sales) >= forecastAlertThreshold
                            ? "text-badge-orange-fg-text"
                            : undefined
                        }
                      />
                    )}
                  {Math.round(phasingOutEffect) !== 0 &&
                    (phasingOutWeeks > 0 ? (
                      <Accordion type="multiple" className="[&>div]:border-none">
                        <HoverCardExpandableRow
                          hideCaret
                          HoverCardRow={
                            <HoverCardRow
                              icon={<TrendDown size={14} />}
                              label={
                                <span className="font-normal">
                                  Phasing out impact (next {phasingOutWeeks}w){" "}
                                  <CaretDown
                                    size={12}
                                    className="inline transition-transform duration-200 group-data-[state=open]/expandable:rotate-180"
                                  />
                                </span>
                              }
                              value={`${formatNumber(Math.round(phasingOutEffect))} (${pct(phasingOutEffect)})`}
                              className={
                                sales > 0 &&
                                Math.abs(phasingOutEffect / sales) >= forecastAlertThreshold
                                  ? "font-medium text-badge-orange-fg-text"
                                  : "font-medium"
                              }
                              valueExtra={
                                <InfoTooltip
                                  body="Phasing-out variants receive no new suggested orders and sell through the remaining stock only. If demand exceeds stock, sales are lost. Expand for the per-size breakdown."
                                  containerClassName="max-w-96"
                                  className="h-3.5 w-3.5 text-fg-muted"
                                />
                              }
                            />
                          }
                        >
                          <PhaseOutSellDownContent variants={variants} />
                        </HoverCardExpandableRow>
                      </Accordion>
                    ) : (
                      <HoverCardRow
                        icon={<TrendDown size={14} />}
                        label="No demand (phased out)"
                        value={`${formatNumber(Math.round(phasingOutEffect))} (${pct(phasingOutEffect)})`}
                        className={
                          sales > 0 && Math.abs(phasingOutEffect / sales) >= forecastAlertThreshold
                            ? "font-medium text-badge-orange-fg-text"
                            : undefined
                        }
                        valueExtra={
                          <InfoTooltip
                            body="Phased-out variants get no demand."
                            className="h-3.5 w-3.5 text-fg-muted"
                          />
                        }
                      />
                    ))}
                </HoverCardRowWrapper>
              </div>
            </HoverCardExpandableRow>
          </Accordion>
        </div>
      </>
    ),
  })
}

function PhaseOutSellDownContent({ variants }: { variants: IVariantSummary[] }) {
  const phasingOutIds = variants
    .filter((v) => v.settings?.lifecycleStatus === "phasingOut")
    .map((v) => v.variantId)
  const variantIds = phasingOutIds.length > 0 ? phasingOutIds : variants.map((v) => v.variantId)
  const { data, isLoading } = usePhaseOutSellDownDetail(variantIds, true)

  const MAX_ROWS = 12
  const fmt = (v: number) => formatNumber(Math.round(v) || 0)
  const compactTable = "[&_.grid>*]:px-1.5 [&_.grid>*]:py-[3px] [&_.paragraph-base]:text-xs"

  if (isLoading) {
    // Skeleton sized to the variant's SKU count (+1 totals row) so the table doesn't jump on load
    const skuCount = variants.reduce((sum, v) => sum + v.skuIds.length, 0)
    const skeletonRows = Math.min(Math.max(skuCount, 1), MAX_ROWS) + (skuCount > 1 ? 1 : 0)
    const skeletonCell = () => (
      <Skeleton className="h-3 my-0.5 w-3/4">
        <span />
      </Skeleton>
    )
    return (
      <div className="py-1.5 pl-4 pr-4">
        <MiniTable
          className={compactTable}
          headers={["Size", "Stock", "Demand (52w)", `Lost ${formatName("sale", true)}`]}
          rows={Array.from({ length: skeletonRows }, () => [
            skeletonCell(),
            skeletonCell(),
            skeletonCell(),
            skeletonCell(),
          ])}
        />
      </div>
    )
  }
  if (!data || data.length === 0) {
    return (
      <div className="paragraph-sm text-fg-muted py-1.5 pl-4 pr-4">
        No sell-down detail available.
      </div>
    )
  }

  // Pick the MAX_ROWS largest losses, then display them in size order
  const rows = [...data].sort(
    (a, b) => a.sellableDemand - a.uncappedDemand - (b.sellableDemand - b.uncappedDemand),
  )
  const shown = rows
    .slice(0, MAX_ROWS)
    .sort((a, b) => sortSizesComparator(a.size ?? "", b.size ?? ""))
  const totals = rows.reduce(
    (acc, r) => ({
      stock: acc.stock + r.stock,
      uncapped: acc.uncapped + r.uncappedDemand,
      lost: acc.lost + (r.sellableDemand - r.uncappedDemand),
    }),
    { stock: 0, uncapped: 0, lost: 0 },
  )
  const lostCell = (lost: number) =>
    Math.round(lost) !== 0 ? (
      <span className="text-badge-orange-fg-text">{fmt(lost)}</span>
    ) : (
      fmt(lost)
    )

  return (
    <div className="py-1.5 pl-4 pr-4">
      <MiniTable
        className={compactTable}
        headers={["Size", "Stock", "Demand (52w)", `Lost ${formatName("sale", true)}`]}
        rows={[
          ...shown.map((r) => [
            r.size ?? r.skuName ?? r.skuId,
            fmt(r.stock),
            fmt(r.uncappedDemand),
            lostCell(r.sellableDemand - r.uncappedDemand),
          ]),
          ...(rows.length > 1
            ? [
                [
                  <span key="t" className="font-medium">
                    Total
                  </span>,
                  <span key="s" className="font-medium">
                    {fmt(totals.stock)}
                  </span>,
                  <span key="d" className="font-medium">
                    {fmt(totals.uncapped)}
                  </span>,
                  <span key="l" className="font-medium">
                    {lostCell(totals.lost)}
                  </span>,
                ],
              ]
            : []),
        ]}
      />
      {rows.length > MAX_ROWS && (
        <div className="paragraph-sm text-fg-muted mt-1">
          +{rows.length - MAX_ROWS} more sizes (largest losses shown)
        </div>
      )}
    </div>
  )
}

function NpiForecastBreakdownHoverCard({
  variant,
  trigger,
}: {
  variant: IVariantSummary
  trigger?: React.ReactNode
}) {
  return (
    <BreakdownHoverCard
      trigger={
        trigger ?? (
          <div className="items-center flex">
            <Info size={14} className="text-fg-muted" />
          </div>
        )
      }
      className="w-80 [&_[data-radix-accordion-content]]:!transition-none"
      scrollable
    >
      <NpiBreakdownSections variant={variant} layout={stackedLayout} />
    </BreakdownHoverCard>
  )
}

function NpiBreakdownSections({
  variant,
  layout,
}: {
  variant: IVariantSummary
  layout: BreakdownLayout
}) {
  const bd = getNpiForecastBreakdown([variant])
  const { forecast, knownPreorders, manualAdjustment, fashionForecast, rawModelForecast } = bd

  return layout({
    title: "NPI demand breakdown",
    identity: <VariantIdentityRow variant={variant} />,
    body: (
      <>
        <HoverCardRowWrapper>
          <HoverCardRow
            icon={<TrendUp size={14} />}
            label="Demand (next 52w)"
            value={formatNumber(Math.round(forecast))}
          />
        </HoverCardRowWrapper>
        <div className="border-t border-base">
          <HoverCardRowWrapper>
            {rawModelForecast != null ? (
              <Accordion type="multiple" className="[&>div]:border-none">
                <HoverCardExpandableRow
                  hideCaret
                  HoverCardRow={
                    <HoverCardRow
                      icon={<ChartLine size={14} />}
                      label={
                        <>
                          Model forecast{" "}
                          <CaretDown
                            size={12}
                            className="inline transition-transform duration-200 group-data-[state=open]/expandable:rotate-180"
                          />
                        </>
                      }
                      value={formatNumber(Math.round(rawModelForecast))}
                    />
                  }
                >
                  <HoverCardRow
                    icon={<span className="w-3.5" />}
                    label={`  Known ${formatName("preorder", true)}`}
                    value={formatNumber(Math.round(knownPreorders))}
                  />
                  <HoverCardRow
                    icon={<span className="w-3.5" />}
                    label="  Forecasted demand"
                    value={formatNumber(Math.round(rawModelForecast - knownPreorders))}
                  />
                </HoverCardExpandableRow>
              </Accordion>
            ) : (
              <HoverCardRow
                icon={<ShoppingCart size={14} />}
                label={`Known ${formatName("preorder", true)}`}
                value={formatNumber(Math.round(knownPreorders))}
              />
            )}
            {Math.round(manualAdjustment) !== 0 && (
              <HoverCardRow
                icon={<PencilSimple size={14} />}
                label="Manual input"
                value={formatNumber(Math.round(manualAdjustment))}
              />
            )}
            {Math.round(fashionForecast) !== 0 && (
              <HoverCardRow
                icon={<TShirt size={14} />}
                label="Fashion Forecast"
                value={formatNumber(Math.round(fashionForecast))}
              />
            )}
          </HoverCardRowWrapper>
        </div>
      </>
    ),
  })
}

function VariantIdentityRow({ variant }: { variant: IVariantSummary }) {
  const displayName = formatProductName(variant.variantName, variant.productName)
  return (
    <div className="flex items-center gap-2 mt-2 min-w-0">
      <div className="shrink-0">
        <ProductImage width={24} url={variant.imageUrl ?? undefined} alt="Variant" />
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <Tooltip
          title={`${displayName ?? ""}\n#${formatVariantId(variant.variantId)}`}
          className="text-left"
        >
          <div className="paragraph-sm text-fg-muted truncate">{displayName}</div>
        </Tooltip>
      </div>
    </div>
  )
}
